/**
 * <video-testimonials>
 * The card carousel is handled by the theme's <swiper-component>.
 * This element only drives the fullscreen "story" lightbox player.
 */
class VideoTestimonials extends HTMLElement {
  constructor() {
    super();
    this.lightbox = null;
    this.lightboxSwiper = null;
    this.activeVideo = null;
    this.previousFocus = null;
    this.scrollY = 0;
    this.originalParent = null;
    this.originalNextSibling = null;
    this.progressAnimationFrame = null;
    this.closeAnimationTimer = null;
    this.fallbackActiveIndex = 0;
    this.lightboxOpenToken = 0;
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.lightbox = this.querySelector('[data-vt-lightbox]');
    this.previewVideos = Array.from(this.querySelectorAll('.vt-card__video'));
    this.visiblePreviewVideos = new Set();
    this.inView = false;
    this.setupPreviewObserver();

    this.querySelectorAll('[data-vt-open]').forEach((button) => {
      button.addEventListener('click', () => this.open(Number(button.dataset.vtIndex || 0)));
    });

    this.querySelectorAll('[data-vt-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.querySelector('[data-vt-prev]')?.addEventListener('click', () => this.previousSlide());
    this.querySelector('[data-vt-next]')?.addEventListener('click', () => this.nextSlide());
    this.querySelector('[data-vt-mute]')?.addEventListener('click', () => this.toggleMute());
    this.querySelector('[data-vt-share]')?.addEventListener('click', () => this.share());
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
    this.previewObserver?.disconnect();
    this.stopProgressAnimation();
    clearTimeout(this.closeAnimationTimer);
    this.pausePreviews();
    this.lightboxSwiper?.destroy(true, true);
    this.lightboxSwiper = null;
    this.unlockScroll();
    this.restoreLightbox();
  }

  /**
   * Load + play the muted card previews only while the section is near the
   * viewport (preload="none" means nothing downloads until the first play).
   */
  setupPreviewObserver() {
    if (!this.previewVideos.length || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.previewObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) return;

          if (entry.isIntersecting) {
            this.visiblePreviewVideos.add(video);
            this.playPreview(video);
          } else {
            this.visiblePreviewVideos.delete(video);
            this.pausePreview(video);
          }
        });
        this.inView = this.visiblePreviewVideos.size > 0;
      },
      { rootMargin: '100px 0px', threshold: 0 }
    );
    this.previewVideos.forEach((video) => this.previewObserver.observe(video));
  }

  playPreviews() {
    // Don't play card previews behind an open lightbox.
    if (this.lightbox && !this.lightbox.hidden) return;
    this.visiblePreviewVideos.forEach((video) => this.playPreview(video));
  }

  playPreview(video) {
    if (this.lightbox && !this.lightbox.hidden) return;
    video.muted = true;
    const playback = video.play();
    if (playback && typeof playback.then === 'function') {
      playback.then(() => video.classList.add('is-playing')).catch(() => {});
    } else {
      video.classList.add('is-playing');
    }
  }

  pausePreview(video) {
    video.pause();
    video.classList.remove('is-playing');
  }

  pausePreviews() {
    this.previewVideos.forEach((video) => this.pausePreview(video));
  }

  open(index = 0) {
    if (!this.lightbox) return;

    this.previousFocus = document.activeElement;
    this.pausePreviews();
    this.moveLightboxToBody();
    this.lightbox.classList.remove('is-closing');
    this.lightbox.hidden = false;
    this.lockScroll();
    document.addEventListener('keydown', this.handleKeydown);

    const openToken = ++this.lightboxOpenToken;
    void this.initLightboxSwiper(index, openToken);
    this.lightbox.querySelector('[data-vt-close]')?.focus();
  }

  close() {
    if (!this.lightbox || this.lightbox.hidden || this.lightbox.classList.contains('is-closing')) return;

    this.pauseAllVideos();
    this.lightboxOpenToken += 1;
    document.removeEventListener('keydown', this.handleKeydown);
    this.lightbox.classList.add('is-closing');

    const finishClose = (event) => {
      if (event && event.target !== this.lightbox) return;
      this.lightbox.removeEventListener('animationend', finishClose);
      clearTimeout(this.closeAnimationTimer);
      this.closeAnimationTimer = null;
      this.lightbox.classList.remove('is-closing');
      this.lightbox.hidden = true;
      this.unlockScroll();
      this.restoreLightbox();

      if (this.previousFocus instanceof HTMLElement) {
        try {
          this.previousFocus.focus({ preventScroll: true });
        } catch (error) {
          this.previousFocus.focus();
        }
      }
      this.restoreScrollPosition();

      // Resume card previews if the section is still in view.
      if (this.inView) this.playPreviews();
    };

    this.lightbox.addEventListener('animationend', finishClose);
    this.closeAnimationTimer = setTimeout(finishClose, 400);
  }

  async initLightboxSwiper(index, openToken) {
    const swiperElement = this.lightbox?.querySelector('.vt-lightbox__swiper');
    if (!swiperElement) return;

    if (!this.lightboxSwiper) {
      // Keep the tapped video visible while the click-triggered module import
      // completes. This also leaves the lightbox usable if that import fails.
      this.showFallbackSlide(index);
      const swiperVendor = await window.loadSwiperVendor?.(['a11y', 'keyboard']);
      if (openToken !== this.lightboxOpenToken || this.lightbox?.hidden) return;
      if (!swiperVendor?.Swiper) return;

      this.lightboxSlides.forEach((slide) => slide.removeAttribute('hidden'));
      this.lightboxSwiper = new swiperVendor.Swiper(swiperElement, {
        modules: swiperVendor.modules,
        slidesPerView: 1,
        speed: 350,
        loop: false,
        keyboard: { enabled: true, onlyInViewport: false },
        on: {
          // The trailing slideTo()/playActiveVideo() below drives the first
          // playback. Playing here too would start a second, competing play()
          // whose interruption re-mutes the video (see playActiveVideo).
          init: (swiper) => {
            this.updateNavigationState(swiper);
          },
          slideChange: (swiper) => {
            this.playActiveVideo();
            this.updateNavigationState(swiper);
          },
        },
      });
    }

    if (this.lightboxSwiper) {
      this.lightboxSwiper.slideTo(index, 0);
      this.playActiveVideo();
      this.updateNavigationState();
    } else {
      this.showFallbackSlide(index);
    }
  }

  updateNavigationState(swiper = this.lightboxSwiper) {
    const previousButton = this.lightbox?.querySelector('[data-vt-prev]');
    const nextButton = this.lightbox?.querySelector('[data-vt-next]');
    const slides = swiper?.slides || this.lightboxSlides;
    const activeIndex = swiper ? swiper.activeIndex : this.fallbackActiveIndex;
    const hasMultipleSlides = slides.length > 1;

    if (previousButton) previousButton.disabled = !hasMultipleSlides || activeIndex === 0;
    if (nextButton) nextButton.disabled = !hasMultipleSlides || activeIndex === slides.length - 1;
  }

  get lightboxSlides() {
    return Array.from(this.lightbox?.querySelectorAll('.vt-lightbox__slide') || []);
  }

  showFallbackSlide(index) {
    const slides = this.lightboxSlides;
    if (!slides.length) return;

    this.fallbackActiveIndex = Math.max(0, Math.min(index, slides.length - 1));
    slides.forEach((slide, slideIndex) => {
      slide.hidden = slideIndex !== this.fallbackActiveIndex;
    });
    this.playActiveVideo();
    this.updateNavigationState();
  }

  previousSlide() {
    if (this.lightboxSwiper) {
      this.lightboxSwiper.slidePrev();
    } else {
      this.showFallbackSlide(this.fallbackActiveIndex - 1);
    }
  }

  nextSlide() {
    if (this.lightboxSwiper) {
      this.lightboxSwiper.slideNext();
    } else {
      this.showFallbackSlide(this.fallbackActiveIndex + 1);
    }
  }

  moveLightboxToBody() {
    if (!this.lightbox || this.lightbox.parentElement === document.body) return;
    this.originalParent = this.lightbox.parentElement;
    this.originalNextSibling = this.lightbox.nextSibling;
    document.body.appendChild(this.lightbox);
  }

  restoreLightbox() {
    if (!this.lightbox || !this.originalParent || this.lightbox.parentElement !== document.body) return;
    this.originalParent.insertBefore(this.lightbox, this.originalNextSibling);
  }

  lockScroll() {
    this.scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    document.documentElement.classList.add('vt-lightbox-open');
    document.body.classList.add('vt-lightbox-open');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollY}px`;
    document.body.style.insetInline = '0';
    document.body.style.width = '100%';
  }

  unlockScroll() {
    if (!document.body.classList.contains('vt-lightbox-open')) return;
    document.documentElement.classList.remove('vt-lightbox-open');
    document.body.classList.remove('vt-lightbox-open');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.insetInline = '';
    document.body.style.width = '';
    this.restoreScrollPosition();
  }

  restoreScrollPosition() {
    const scrollPosition = this.scrollY || 0;
    const restore = () => window.scrollTo({ top: scrollPosition, left: 0, behavior: 'auto' });
    restore();
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
    });
  }

  get activeSlide() {
    return (
      this.lightboxSwiper?.slides?.[this.lightboxSwiper.activeIndex] ||
      this.lightboxSlides[this.fallbackActiveIndex]
    );
  }

  playActiveVideo() {
    this.pauseAllVideos();

    const video = this.activeSlide?.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    // Generation token: if a newer playback starts before this one's play()
    // promise settles, the stale rejection below must not re-mute the current
    // video. This is what previously left lightbox videos muted.
    const token = (this.playToken || 0) + 1;
    this.playToken = token;

    this.activeVideo = video;
    video.loop = false;
    video.controls = false;
    // Open the lightbox muted by default; the user can unmute via the sound
    // button. Muted playback also guarantees autoplay is never blocked.
    video.muted = true;
    video.volume = 1;
    video.onended = () => this.handleVideoEnded();
    video.onplay = () => this.startProgressAnimation();
    video.onloadedmetadata = () => this.updateProgress();

    video.play().catch(() => {
      // Only handle this rejection if it belongs to the latest play request;
      // otherwise a superseded call would mute the now-active video.
      if (this.playToken !== token) return;
      // Autoplay with sound was genuinely blocked; fall back to muted playback.
      video.muted = true;
      video.play().catch(() => {});
      this.updateMuteButton();
    });

    this.updateProgress();
    this.updateMuteButton();
  }

  pauseAllVideos() {
    this.stopProgressAnimation();
    this.lightbox?.querySelectorAll('video').forEach((video) => {
      video.pause();
      video.currentTime = 0;
      video.onended = null;
      video.onplay = null;
      video.onloadedmetadata = null;
    });
    this.activeVideo = null;
    this.updateProgress();
  }

  handleVideoEnded() {
    const slides = this.lightboxSwiper?.slides || this.lightboxSlides;
    const activeIndex = this.lightboxSwiper ? this.lightboxSwiper.activeIndex : this.fallbackActiveIndex;
    if (!slides.length) return;

    if (activeIndex < slides.length - 1) {
      this.nextSlide();
    } else {
      if (this.lightboxSwiper) {
        this.lightboxSwiper.slideTo(0);
      } else {
        this.showFallbackSlide(0);
      }
    }
  }

  toggleMute() {
    if (!this.activeVideo) return;
    this.activeVideo.muted = !this.activeVideo.muted;
    this.updateMuteButton();
  }

  updateMuteButton() {
    const button = this.lightbox?.querySelector('[data-vt-mute]');
    const label = this.lightbox?.querySelector('[data-vt-mute-label]');
    if (!button) return;
    const muted = this.activeVideo ? this.activeVideo.muted : true;
    button.classList.toggle('is-muted', muted);
    button.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
    if (label) label.textContent = muted ? 'Sound' : 'Mute';
  }

  updateProgress() {
    const bar = this.lightbox?.querySelector('[data-vt-progress]');
    if (!bar) return;
    const video = this.activeVideo;
    if (!video || !Number.isFinite(video.duration) || video.duration === 0) {
      bar.style.transform = 'scaleX(0)';
      return;
    }
    bar.style.transform = `scaleX(${video.currentTime / video.duration})`;
  }

  startProgressAnimation() {
    this.stopProgressAnimation();

    const update = () => {
      this.updateProgress();
      if (this.activeVideo && !this.activeVideo.paused && !this.activeVideo.ended) {
        this.progressAnimationFrame = requestAnimationFrame(update);
      } else {
        this.progressAnimationFrame = null;
      }
    };

    this.progressAnimationFrame = requestAnimationFrame(update);
  }

  stopProgressAnimation() {
    if (this.progressAnimationFrame === null) return;
    cancelAnimationFrame(this.progressAnimationFrame);
    this.progressAnimationFrame = null;
  }

  async share() {
    const slide = this.activeSlide;
    const url = slide?.dataset.vtShareUrl || window.location.href;
    const title = slide?.dataset.vtShareTitle || document.title;
    const absoluteUrl = new URL(url, window.location.origin).href;

    if (navigator.share) {
      try {
        await navigator.share({ title, url: absoluteUrl });
      } catch (error) {
        /* user cancelled */
      }
      return;
    }

    try {
      await navigator.clipboard.writeText(absoluteUrl);
      const button = this.lightbox?.querySelector('[data-vt-share] .vt-lightbox__action-label');
      if (button) {
        const original = button.textContent;
        button.textContent = 'Copied';
        setTimeout(() => (button.textContent = original), 1500);
      }
    } catch (error) {
      window.open(absoluteUrl, '_blank', 'noopener');
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      // If a cart drawer or quick-add popup is open on top, let it handle Escape.
      if (document.querySelector('cart-drawer.active') || document.querySelector('[element-s-popup][open]')) return;
      this.close();
    } else if (event.key === 'ArrowLeft') {
      this.previousSlide();
    } else if (event.key === 'ArrowRight') {
      this.nextSlide();
    }
  }
}

if (!customElements.get('video-testimonials')) {
  customElements.define('video-testimonials', VideoTestimonials);
}
